import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Flame, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";

export default function Prayer() {
  const { t } = useI18n();
  const [pledges, setPledges] = useState(0);
  const [intentions, setIntentions] = useState([]);
  const [pledgeForm, setPledgeForm] = useState({ name: "", email: "" });
  const [intForm, setIntForm] = useState({ name: "", text: "" });
  const [pledgeOpen, setPledgeOpen] = useState(false);
  const [intOpen, setIntOpen] = useState(false);

  const load = () => {
    api.get("/prayer/stats").then((r) => setPledges(r.data.pledges));
    api.get("/prayer/intentions").then((r) => setIntentions(r.data));
  };
  useEffect(() => { load(); }, []);

  const submitPledge = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/prayer/pledge", pledgeForm);
      setPledges(data.pledges);
      setPledgeOpen(false);
      setPledgeForm({ name: "", email: "" });
      toast.success("🙏 " + t("prayer_cta"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const submitIntention = async (e) => {
    e.preventDefault();
    try {
      await api.post("/prayer/intentions", intForm);
      setIntOpen(false);
      setIntForm({ name: "", text: "" });
      toast.success(t("intention_success"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <section className="relative bg-th-bluedark text-white overflow-hidden">
        <div className="th-hero-glow absolute inset-0" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <Flame className="w-12 h-12 text-th-gold mx-auto mb-4 th-float" />
          <h1 className="font-display font-bold text-4xl sm:text-6xl">{t("prayer_title")}</h1>
          <p className="mt-5 text-white/75 text-lg max-w-2xl mx-auto">{t("prayer_subtitle")}</p>

          <motion.div key={pledges} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-10">
            <div className="font-display font-bold text-5xl sm:text-7xl text-th-gold" data-testid="prayer-counter">{pledges.toLocaleString()}</div>
            <div className="text-white/60 uppercase tracking-widest text-sm mt-2">{t("prayer_pledged")}</div>
          </motion.div>

          <Dialog open={pledgeOpen} onOpenChange={setPledgeOpen}>
            <DialogTrigger asChild>
              <Button size="lg" data-testid="pray-now-button" className="mt-8 bg-th-gold text-th-bluedark hover:bg-th-goldlight rounded-full px-10 h-13 text-lg font-semibold">
                <Heart className="w-5 h-5 mr-2" />{t("prayer_cta")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="text-th-blue">{t("prayer_cta")}</DialogTitle></DialogHeader>
              <form onSubmit={submitPledge} className="space-y-4">
                <div><Label>{t("form_name")}</Label><Input required value={pledgeForm.name} data-testid="pledge-name" onChange={(e) => setPledgeForm({ ...pledgeForm, name: e.target.value })} className="mt-1" /></div>
                <div><Label>{t("form_email")} <span className="text-slate-400 text-xs">(opcional)</span></Label><Input type="email" value={pledgeForm.email} data-testid="pledge-email" onChange={(e) => setPledgeForm({ ...pledgeForm, email: e.target.value })} className="mt-1" /></div>
                <DialogFooter><Button type="submit" data-testid="pledge-submit" className="bg-th-blue text-white">{t("prayer_cta")}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display font-semibold text-2xl sm:text-3xl text-th-blue">{t("prayer_intentions")}</h2>
          <Dialog open={intOpen} onOpenChange={setIntOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-intention-button" className="border-th-blue text-th-blue rounded-full"><Send className="w-4 h-4 mr-2" />{t("prayer_add_intention")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="text-th-blue">{t("prayer_add_intention")}</DialogTitle></DialogHeader>
              <form onSubmit={submitIntention} className="space-y-4">
                <div><Label>{t("form_name")}</Label><Input required value={intForm.name} data-testid="intention-name" onChange={(e) => setIntForm({ ...intForm, name: e.target.value })} className="mt-1" /></div>
                <div><Label>{t("prayer_intentions")}</Label><Textarea required value={intForm.text} data-testid="intention-text" onChange={(e) => setIntForm({ ...intForm, text: e.target.value })} placeholder={t("prayer_intention_placeholder")} className="mt-1" rows={3} /></div>
                <DialogFooter><Button type="submit" data-testid="intention-submit" className="bg-th-blue text-white">{t("submit")}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="grid sm:grid-cols-2 gap-5" data-testid="intentions-list">
          {intentions.map((it) => (
            <div key={it.id} data-testid={`intention-${it.id}`} className="bg-white rounded-xl p-6 border border-slate-200 border-l-4 border-l-th-gold">
              <p className="text-slate-700 italic">"{it.text}"</p>
              <div className="mt-3 text-sm font-medium text-th-blue">— {it.name}</div>
            </div>
          ))}
          {intentions.length === 0 && <p className="text-slate-500">—</p>}
        </div>
      </section>
    </div>
  );
}
